import { useCallback, useEffect, useSyncExternalStore } from "react";
import { getStoredLang, setStoredLang, t as translate, type Lang } from "@/lib/i18n";
import { trackEvent } from "@/lib/audit";

/**
 * FASE 1+4 — REAL RUNTIME I18N.
 * One single source of truth (localStorage `ss2026:lang`) shared by every
 * subscriber via useSyncExternalStore. Guarantees:
 *  - SSR-safe initial render (always "ES" server-side, hydrates to stored).
 *  - Instant runtime switching across every mounted component.
 *  - Cross-tab sync via the native `storage` event.
 */
function subscribe(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onLang = () => cb();
  const onStorage = (e: StorageEvent) => {
    if (e.key === "ss2026:lang") cb();
  };
  window.addEventListener("ss2026:lang", onLang);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener("ss2026:lang", onLang);
    window.removeEventListener("storage", onStorage);
  };
}

const getSnapshot = (): Lang => getStoredLang();
const getServerSnapshot = (): Lang => "ES";

export function useLanguage() {
  const lang = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Reflect language on <html lang> so a11y, browser UI, and CSS :lang() match.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const code = lang === "EN" ? "en" : lang === "PT" ? "pt" : "es";
    document.documentElement.setAttribute("lang", code);
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    setStoredLang(next);
    void trackEvent("language_changed", { to: next });
  }, []);

  const t = useCallback(
    (key: Parameters<typeof translate>[0], params?: Record<string, string | number>) =>
      translate(key, lang, params),
    [lang],
  );

  return { lang, setLang, t };
}